import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './RefundKeys.css';

const RefundKeys = ({ hasPermission }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [selectedItem, setSelectedItem] = useState('');
    const [amount, setAmount] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedItems, setSelectedItems] = useState([]);
    const [generatedKeys, setGeneratedKeys] = useState([]);
    const [keyHistory, setKeyHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [userData, setUserData] = useState(null);
    const [expandedCategories, setExpandedCategories] = useState({
        handguns: true, smg: true, shotguns: true, rifles: true, snipers: true, heavy: true, melee: true, throwables: true, items: true
    });

    useEffect(() => { fetchItems(); fetchUserData(); }, []);

    // Berechtigung prüfen
    if (!hasPermission('manage_refunds')) {
        return <div className="refund-access-denied">Zugriff verweigert. Sie haben keine Berechtigung für diesen Bereich.</div>;
    }

    const fetchItems = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('discord_token');
            const response = await axios.get('/api/refund-keys/itemlist', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setItems(response.data.items || []);
        } catch (error) { setMessage('Fehler beim Laden.'); } finally { setLoading(false); }
    };

    const fetchUserData = async () => {
        const token = localStorage.getItem('discord_token');
        if (!token) return;
        try {
            const response = await axios.get('/api/user-data', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUserData(response.data);
        } catch (err) {
            console.error('Failed to fetch user data', err);
        }
    };

    const getItemCategory = (itemName) => {
        if (!itemName.startsWith('WEAPON_')) return 'items';
        const w = itemName.replace('WEAPON_', '');
        if (['PISTOL', 'COMBATPISTOL', 'APPISTOL', 'PISTOL50', 'SNSPISTOL', 'HEAVYPISTOL', 'VINTAGEPISTOL', 'MARKSMANPISTOL', 'REVOLVER', 'REVOLVER_MK2', 'DOUBLEACTION', 'CERAMICPISTOL', 'NAVYREVOLVER', 'GADGETPISTOL', 'PISTOL_MK2', 'SNSPISTOL_MK2', 'HEAVYPISTOL_MK2'].includes(w)) return 'handguns';
        if (['MICROSMG', 'SMG', 'ASSAULTSMG', 'MINISMG', 'MACHINEPISTOL', 'COMBATPDW'].includes(w)) return 'smg';
        if (['PUMPSHOTGUN', 'SAWNOFFSHOTGUN', 'BULLPUPSHOTGUN', 'ASSAULTSHOTGUN', 'MUSKET', 'HEAVYSHOTGUN', 'DBSHOTGUN', 'SWEEPERSHOTGUN', 'PUMPSHOTGUN_MK2'].includes(w)) return 'shotguns';
        if (['ASSAULTRIFLE', 'CARBINERIFLE', 'ADVANCEDRIFLE', 'SPECIALCARBINE', 'BULLPUPRIFLE', 'COMPACTRIFLE', 'ASSAULTRIFLE_MK2', 'CARBINERIFLE_MK2',  'BULLPUPRIFLE_MK2'].includes(w)) return 'rifles';
        return 'weapons';
    };

    const groupedItems = () => {
        const groups = { handguns: [], smg: [], shotguns: [], rifles: [], snipers: [], heavy: [], melee: [], throwables: [], items: [] };
        items.filter(i => i.label.toLowerCase().includes(searchTerm.toLowerCase()) || i.name.toLowerCase().includes(searchTerm.toLowerCase())).forEach(i => {
            const cat = getItemCategory(i.name);
            if (groups[cat]) groups[cat].push(i);
        });
        return groups;
    };

    const generateKey = async () => {
        const isBundle = selectedItems.length > 0;
        const item = items.find(i => i.name === selectedItem);
        
        // Validierung: Entweder Bundle-Liste ODER Einzel-Item muss da sein
        if (!isBundle && (!selectedItem || !amount)) {
            setMessage('Fehler: Wähle ein Item oder fülle die Liste.');
            return;
        }

        setLoading(true);
        try {
            const payload = isBundle ? {
                itemName: "BUNDLE",
                amount: 1,
                bundleData: selectedItems,
                creator: userData?.displayname || userData?.global_name || 'Unknown',
                creatorId: userData?.id
            } : {
                itemName: selectedItem,
                amount: parseInt(amount),
                label: item?.label || selectedItem,
                creator: userData?.displayname || userData?.global_name || 'Unknown',
                creatorId: userData?.id
            };

            const response = await axios.post('/api/refund-keys/generate-key', payload, {
                headers: { Authorization: `Bearer ${localStorage.getItem('discord_token')}` }
            });
            
            const displayLabel = isBundle ? `📦 Bundle: ${selectedItems.map(i => `${i.label} x${i.amount}`).join(', ')}` : selectedItem;
            setGeneratedKeys(prev => [{ code: response.data.code, item: displayLabel, amount: payload.amount }, ...prev]);
            
            // Felder zurücksetzen
            setSelectedItem(''); 
            setAmount('');
            setSelectedItems([]); 
            setMessage('✅ Key erfolgreich erstellt!');
        } catch (e) { 
            setMessage('❌ Fehler beim Erstellen.'); 
        } finally { 
            setLoading(false); 
        }
    };

    const addItemToBulk = () => {
        if (!selectedItem || !amount) return;
        const item = items.find(i => i.name === selectedItem);
        setSelectedItems(prev => [...prev, { name: selectedItem, amount: parseInt(amount), label: item?.label || selectedItem }]);
        setAmount('');
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => setMessage('📋 In Zwischenablage kopiert!'));
    };

    const isMessageError = message.includes('Fehler') || message.includes('❌');

    return (
        <div className="refund-keys-container">
            
            {/* Header */}
            <div className="refund-header">
                <h2>salerno City Rückerstattung</h2>
                <button className="refund-history-btn" onClick={async () => {
                    const token = localStorage.getItem('discord_token');
                    const res = await axios.get('/api/refund-keys/keys', {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setKeyHistory(res.data.keys);
                    setShowHistory(true);
                }}>
                    📜 History einsehen
                </button>
            </div>

            {message && <div className={`refund-message ${isMessageError ? 'error' : 'success'}`}>{message}</div>}

            <div className="refund-grid">
                
                {/* GENERATOR BEREICH */}
                <div className="refund-panel">
                    <h3 className="refund-panel-title">🔑 Key erstellen</h3>

                    <div className="refund-logged-user">
                        <div className="refund-logged-user-label">Automatisch geloggte Daten</div>
                        <div className="refund-logged-user-name">{userData?.displayname || userData?.global_name || 'Unknown'}</div>
                        {userData?.id && <div className="refund-logged-user-id">Discord ID: {userData.id}</div>}
                    </div>

                    <div className="refund-form-row">
                        <div className="refund-form-group">
                            <label>Item auswählen</label>
                            <select 
                                value={selectedItem} 
                                onChange={e => setSelectedItem(e.target.value)}
                                className="refund-select"
                            >
                                <option value="">Wähle ein Item...</option>
                                {items.map((i, idx) => <option key={`opt-${i.name}-${idx}`} value={i.name}>{i.label}</option>)}
                            </select>
                        </div>
                        
                        <div className="refund-form-group">
                            <label>Menge</label>
                            <input 
                                type="number" 
                                placeholder="0" 
                                value={amount} 
                                onChange={e => setAmount(e.target.value)}
                                className="refund-input"
                            />
                        </div>

                        <button 
                            onClick={addItemToBulk} 
                            disabled={!selectedItem || !amount}
                            className="refund-btn refund-btn-add"
                        >
                            ➕ Zur Liste
                        </button>

                        <button 
                            onClick={generateKey} 
                            disabled={loading || (selectedItems.length === 0 && (!selectedItem || !amount))}
                            className={`refund-btn refund-generate ${selectedItems.length > 0 ? 'bundle' : ''}`}
                        >
                            {loading ? '...' : (selectedItems.length > 0 ? '🎁 Bundle erstellen' : '🔑 Key erstellen')}
                        </button>
                    </div>

                    {/* Bundle-Liste Anzeige */}
                    {selectedItems.length > 0 && (
                        <div className="refund-bundle-list">
                            <div className="refund-bundle-header">
                                <span>Vorgemerkte Items für Bundle:</span>
                                <span className="refund-bundle-clear" onClick={() => setSelectedItems([])}>Alles löschen</span>
                            </div>
                            <div className="refund-bundle-items">
                                {selectedItems.map((item, idx) => (
                                    <span key={`bulk-${idx}`} className="refund-bundle-item">
                                        <span className="refund-bundle-item-name">{item.name} x{item.amount}</span>
                                        <span className="refund-bundle-item-remove" onClick={() => setSelectedItems(prev => prev.filter((_, i) => i !== idx))}>✕</span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Zuletzt generierte Keys (Quick Copy) */}
                    {generatedKeys.length > 0 && (
                        <div className="refund-generated">
                            <div className="refund-generated-label">Zuletzt erstellt:</div>
                            {generatedKeys.slice(0, 3).map((k, i) => (
                                <div key={`gen-${i}`} className="refund-generated-item">
                                    <div className="refund-generated-info">
                                        <span className="refund-generated-name">{k.item}</span>
                                    </div>
                                    <div className="refund-generated-actions">
                                        <code className="refund-generated-code">{k.code}</code>
                                        <button className="refund-copy-btn" onClick={() => copyToClipboard(`/redeem ${k.code}`)}>📋</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* ITEM VERZEICHNIS */}
                <div className="refund-panel">
                    <div className="refund-directory">
                        <div className="refund-directory-header">
                            <h3 className="refund-panel-title">📦 Item Verzeichnis</h3>
                            <input 
                                type="text" 
                                placeholder="🔍 Item suchen..." 
                                value={searchTerm} 
                                onChange={e => setSearchTerm(e.target.value)}
                                className="refund-search"
                            />
                        </div>

                        <div className="refund-categories">
                            {Object.entries(groupedItems()).map(([cat, itms]) => itms.length > 0 && (
                                <div key={`cat-${cat}`} className="refund-category">
                                    <div 
                                        className="refund-category-header"
                                        onClick={() => setExpandedCategories(p => ({...p, [cat]: !p[cat]}))}
                                    >
                                        <span>{cat.toUpperCase()} ({itms.length})</span>
                                        <span className="refund-category-toggle">{expandedCategories[cat] ? '−' : '+'}</span>
                                    </div>
                                    {expandedCategories[cat] && (
                                        <div className="refund-category-items">
                                            {itms.map((i, idx) => (
                                                <div 
                                                    key={`item-${i.name}-${idx}`}
                                                    className="refund-item"
                                                    onClick={() => {setSelectedItem(i.name); window.scrollTo({top:0, behavior:'smooth'})}}
                                                >
                                                    <span className="refund-item-name">{i.label}</span>
                                                    <span className="refund-item-label">{i.name}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* History Modal */}
            {showHistory && (
                <div className="refund-modal-overlay" onClick={() => setShowHistory(false)}>
                    <div className="refund-modal" onClick={e => e.stopPropagation()}>
                        <div className="refund-modal-header">
                            <h3 className="refund-modal-title">📜 Letzte 50 Keys</h3>
                            <button className="refund-modal-close" onClick={() => setShowHistory(false)}>✕</button>
                        </div>
                        <div className="refund-modal-content">
                            <table className="refund-history-table">
                                <thead>
                                    <tr>
                                        <th>Code</th>
                                        <th>Inhalt</th>
                                        <th>Menge</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {keyHistory.map((k, i) => (
                                        <tr key={`hist-${k.id || i}`}>
                                            <td><code className="refund-history-code">{k.code}</code></td>
                                            <td>{k.item_name === 'BUNDLE' ? `📦 Bundle: ${JSON.parse(k.bundle_data || '[]').map(item => `${item.label || item.name} x${item.amount}`).join(', ')}` : k.item_name}</td>
                                            <td>{k.amount}</td>
                                            <td>
                                                {k.redeemed_at 
                                                    ? <span className="refund-history-status redeemed">Eingelöst</span> 
                                                    : <span className="refund-history-status active">Aktiv</span>
                                                }
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RefundKeys;
